import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState([]);

  const refresh = useCallback(() => {
    if (!user || user === false) { setIds([]); return; }
    api.get("/favorites/ids").then((r) => setIds(r.data || [])).catch((err) => {
      console.error("Failed to load favorites:", err);
      setIds([]);
    });
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFavorite = useCallback((bookId) => ids.includes(bookId), [ids]);

  const toggle = useCallback(async (bookId, bookTitle) => {
    if (!user || user === false) {
      toast("Sign in to save favorites");
      return;
    }
    const wasFav = ids.includes(bookId);
    // Optimistic
    setIds((cur) => wasFav ? cur.filter((x) => x !== bookId) : [...cur, bookId]);
    try {
      if (wasFav) {
        await api.delete(`/favorites/${bookId}`);
        toast(`Removed${bookTitle ? ` "${bookTitle}"` : ""} from favorites`);
      } else {
        await api.post(`/favorites/${bookId}`);
        toast.success(`Saved${bookTitle ? ` "${bookTitle}"` : ""} to favorites`);
      }
    } catch (err) {
      console.error("Favorites toggle failed:", err);
      // Revert on error
      setIds((cur) => wasFav ? [...cur, bookId] : cur.filter((x) => x !== bookId));
      toast.error("Could not update favorites");
    }
  }, [user, ids]);

  const value = useMemo(
    () => ({ ids, isFavorite, toggle, refresh }),
    [ids, isFavorite, toggle, refresh]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
